import React from 'react'
import cx from 'classnames'
import { bool, func, string } from 'prop-types'

import { Button } from '@wf-common/atoms'

import GridCol from '../grid/grid-col'
import GridContainer from '../grid/grid-container'
import GridRow from '../grid/grid-row'
import { SpinnerBar } from '../spinner-bar/spinner-bar'

import styles from './action-area.module.css'

const propTypes = {
    className: string,
    isNextButtonDisabled: bool,
    isPreviousButtonDisabled: bool,
    onBackClicked: func,
    onNextClicked: func.isRequired,
    onPreviousClicked: func.isRequired,
    textString: string,
    tooltipNextText: string,
    tooltipPreviousText: string,
    translatedBack: string
}

const defaultProps = {
    isNextButtonDisabled: false,
    isPreviousButtonDisabled: false
}

/**
 * Render a single ActionArea component.
 *
 * @example <caption>Simple usage</caption>
 * <code>
 * <ActionArea testString="Trip 1 Of 10" onBackClicked={onBackClicked}
    onNextClicked={onNextClicked} onPreviousClicked={onPreviousClicked} />
 * </code>
 *
 * The textString property should update when the next or previous Button click
 *
 * @param {string} [translatedBack]               - The string that we write on Back button and
                                                    it depending on the selected language
 * @param {Function} [onBackClicked]              - The callback to use when the user click back Button
 * @param {string} [testString]                   - String to be print in the Component
 * @param {boolean} [isNextButtonDisabled=false]  - If the next button should be disabled
 * @param {boolean} [isPreviousButtonDisabled=false] - If the previous button should be disabled
 * @param {Function} onNextClicked                - The callback to use when the user click next Button
 * @param {Function} onPreviousClicked            - The callback to use when the user click previous Button
 * @returns {ReactElement}
 */

function ActionArea({
    className,
    isNextButtonDisabled,
    isPreviousButtonDisabled,
    onBackClicked,
    onNextClicked,
    onPreviousClicked,
    textString,
    tooltipNextText,
    tooltipPreviousText,
    translatedBack
}) {
    const numberOfColumns = onBackClicked ? 9 : 12
    const composedClassName = cx(styles['action-area'], className)

    return (
        <GridContainer>
            <div className={composedClassName}>
                <GridRow>
                    <GridCol sm="3" md="3" lg="3">
                        {onBackClicked && translatedBack && (
                            <Button onClick={onBackClicked} className="t3sel-action-area-back-button">
                                {translatedBack}
                            </Button>
                        )}
                    </GridCol>
                    <GridCol sm={numberOfColumns} md={numberOfColumns} lg={numberOfColumns}>
                        <div className={styles['content']}>
                            {textString && (
                                <span className={cx(styles['pagination-text'], 't3sel-action-area-pagination-text')}>
                                    {textString}
                                </span>
                            )}
                            <SpinnerBar
                                prevProps={
                                    onPreviousClicked && {
                                        className: 't3sel-action-area-previous-button',
                                        disabled: isPreviousButtonDisabled,
                                        onClick: onPreviousClicked,
                                        tooltipText: tooltipPreviousText
                                    }
                                }
                                nextProps={
                                    onNextClicked && {
                                        className: 't3sel-action-area-next-button',
                                        disabled: isNextButtonDisabled,
                                        onClick: onNextClicked,
                                        tooltipText: tooltipNextText
                                    }
                                }
                            />
                        </div>
                    </GridCol>
                </GridRow>
            </div>
        </GridContainer>
    )
}

ActionArea.propTypes = propTypes
ActionArea.defaultProps = defaultProps

export { ActionArea }
